/** Preserve category identity while removing recognizable dimensions and model codes. */
const categoryNouns = [
    "冷水管", "热水管", "燃气管", "排水管", "给水管", "水管", "软管",
    "等径直通", "异径直通", "内牙直通", "外牙直通", "直通",
    "带座内牙弯头", "内牙弯头", "外牙弯头", "等径弯头", "异径弯头", "弯头", "过桥弯",
    "等径三通", "异径三通", "内牙三通", "顺水三通", "三通", "四通",
    "堵头", "管帽", "活接", "接头", "扣座", "角阀", "截止阀", "球阀", "阀门",
    "龙头", "花洒", "喷枪", "地漏", "弹簧", "整圆器", "过滤器", "水表", "水泵",
].sort((left, right) => right.length - left.length);
function joinBrandAndCore(brand, core) {
    for (let overlap = Math.min(brand.length, core.length); overlap >= 2; overlap -= 1) {
        if (brand.endsWith(core.slice(0, overlap)))
            return `${brand}${core.slice(overlap)}`;
    }
    return `${brand}${core}`;
}
function commonPrefix(values) {
    let result = values[0] ?? "";
    for (const value of values.slice(1)) {
        while (result && !value.startsWith(result))
            result = result.slice(0, -1);
    }
    return result;
}
function parseCategoryName(value) {
    let source = value.normalize("NFKC").trim().replace(/[‐‑–—]/g, "-")
        .replace(/[【[{<]/g, "(").replace(/[】\]}>]/g, ")").replace(/^[×*☆★※\s]+/, "");
    let brand = "";
    const leading = source.match(/^\(((?:[^()]|\([^()]*\))*)\)\s*/);
    if (leading && /\p{Script=Han}/u.test(leading[1])) {
        brand = leading[1].replace(/[()☆★※\s]+/g, "");
        source = source.slice(leading[0].length);
    }
    const protectedParts = [];
    const protect = (part) => {
        protectedParts.push(part.replace(/\s+/g, ""));
        return String.fromCharCode(0xe000 + protectedParts.length - 1);
    };
    // Remove a capacity/dimension bracket as a unit, before its digits disappear.
    // In particular, (2-4位) and (26-30位双层) must not leave a false “（位）” caption.
    source = source.replace(/\((?=[^()]*\d)[\dA-Za-zΦφФфØø.\-_/×*"'′″\s]*(?:毫米|厘米|米|寸|吋|分|位|回路|克|千克|公斤|毫升|升)(?:[\dA-Za-z.\-_/×*\s]*(?:单层|双层|多层|层|位|回路))?\)/g, "")
        .replace(/\((?:大|中|小)(?:型|号)?\)/g, "");
    // Angles, materials and stainless-steel grades are identities, not size variants.
    source = source.replace(/\d+(?:\.\d+)?\s*(?:°|度)/g, protect)
        .replace(/\b(?:PP-R|PE-RT|PVC-U|UPVC|PPSU|PPR|PERT|HDPE|PVC)\b/gi, protect)
        .replace(/(?:201|304|316L?)\s*(?=不锈钢)/g, protect)
        .replace(/[A-Za-z]\s*型|[A-Z]\s*\+(?=\p{Script=Han})/gu, protect)
        .replace(/\d+\s*(?:层|路|孔|通|合一|芯|色|级|代)|\d+(?:\.\d+)?\s*℃/g, protect);
    source = source.replace(/\d+(?:\.\d+)?(?:\s*[-~～]\s*\d+(?:\.\d+)?)?\s*(?:毫米|厘米|千克|公斤|毫升|回路|米|寸|吋|分|位|克|升)/g, "");
    source = source.replace(/[A-Za-z0-9ΦφФфØø][A-Za-z0-9ΦφФфØø.\-_/×*"'′″\s]*/g, (token) => {
        if (!/\d/.test(token))
            return token;
        // F12-L20×20, RF-1009P, D20×2.0, Φ25, 1/2" and 20*25.
        return "";
    });
    source = source.replace(/\((?:[A-Za-z][A-Za-z\d-]*|\d+(?:\.\d+)?)\)/g, "")
        .replace(/^[A-Z]{1,2}(?=\p{Script=Han})/u, "")
        .replace(/\(\s*\)/g, "")
        .replace(/\s+/g, "")
        .replace(/[☆★※]/g, "");
    source = source.replace(/[\ue000-\uf8ff]/g, (marker) => protectedParts[marker.charCodeAt(0) - 0xe000] ?? marker)
        .replace(/\(/g, "（").replace(/\)/g, "）")
        .replace(/[-×*]+(?=（)/g, "")
        .replace(/^[-×*]+|[-×*]+$/g, "");
    return { brand, core: source, name: joinBrandAndCore(brand, source) };
}
export function normalizeCategoryProductName(value) {
    return parseCategoryName(value).name;
}
function sharedBrand(parts) {
    let brand = commonPrefix(parts.map((part) => part.brand)).replace(/[-\s]+$/, "");
    if (!parts.some((part) => part.brand === brand)) {
        const material = brand.match(/^(.*(?:PP-R|PE-RT|PVC-U|UPVC|PPSU|PPR|PERT|HDPE|PVC))/i);
        if (material)
            brand = material[1];
        else if (brand.length > 3)
            brand = "";
    }
    return brand.length >= 2 ? brand : "";
}
function plainCore(part) {
    return part.core.replace(/（[^（）]*）/g, "");
}
function isCategoryTerm(candidate, parts) {
    return /\p{Script=Han}{2}/u.test(candidate)
        && !/^(?:纯铜|黄铜|铜|不锈钢|塑料|塑胶|铝合金|铝塑|铝|透明|白色|黑色|家装精品|精品|等径|异径|内牙|外牙|缩口)$/.test(candidate)
        && !parts.some((part) => part.brand === candidate);
}
/** Look beyond the noun vocabulary: the repeated product text is the evidence. */
function sharedProductTerm(parts) {
    const cores = parts.map(plainCore);
    const shortest = [...cores].sort((left, right) => left.length - right.length || left.localeCompare(right))[0];
    for (let length = shortest.length; length >= 2; length -= 1) {
        for (let start = 0; start + length <= shortest.length; start += 1) {
            const candidate = shortest.slice(start, start + length);
            const splitsQualifier = cores.every((core) => {
                const position = core.indexOf(candidate);
                return position > 0 && /^(?:暗装|明装|等径|异径|内牙|外牙|冷水|热水|大号|中号|小号|高档|中档|低档)$/.test(core.slice(position - 1, position + 1));
            });
            // A shared material/adjective prefix alone is not a product category.
            if (!splitsQualifier && isCategoryTerm(candidate, parts) && cores.some((core) => core.endsWith(candidate))
                && cores.every((core) => core.includes(candidate)))
                return candidate;
        }
    }
    return undefined;
}
function inferSharedName(parts) {
    if (parts.every((part) => part.name === parts[0].name))
        return parts[0].name;
    const noun = categoryNouns.find((candidate) => parts.every((part) => part.core.includes(candidate)));
    if (!noun) {
        const term = sharedProductTerm(parts);
        if (!term)
            return undefined;
        const qualifiers = (parts[0].core.match(/（[^（）]+）/g) ?? [])
            .filter((qualifier) => parts.every((part) => part.core.includes(qualifier)));
        return joinBrandAndCore(sharedBrand(parts), `${term}${qualifiers.join("")}`);
    }
    const befores = parts.map((part) => part.core.slice(0, part.core.indexOf(noun)));
    const before = commonPrefix(befores);
    // An incomplete bracket or a single character from a differing qualifier is not a name.
    const prefix = befores.every((value) => value === before) ? before
        : before.match(/^(?:纯铜|黄铜|铜|不锈钢|塑料|塑胶|铝合金|铝塑|铝)/)?.[0] ?? "";
    const suffixes = parts.map((part) => part.core.slice(part.core.indexOf(noun) + noun.length));
    const suffix = suffixes.every((value) => value === suffixes[0]) ? suffixes[0]
        : (suffixes[0].match(/（[^（）]+）/g) ?? []).filter((qualifier) => suffixes.every((value) => value.includes(qualifier))).join("");
    return joinBrandAndCore(sharedBrand(parts), `${prefix}${noun}${suffix}`);
}
/** Prefer a shared family, then a dominant family supported by two thirds of products. */
export function inferProductCategoryName(names) {
    if (!names.length || names.some((name) => !name.trim()))
        return undefined;
    // Each product is a vote, even when several products have identical source names.
    // buildCodeCategories already deduplicates repeated rows by product ID.
    const parts = names.map(parseCategoryName);
    if (parts.some((part) => !/\p{Script=Han}{2}/u.test(part.core)))
        return undefined;
    let name = inferSharedName(parts);
    if (!name) {
        const candidates = [...new Set([...categoryNouns, ...parts.map(plainCore)])]
            .filter((candidate) => isCategoryTerm(candidate, parts))
            .map((candidate) => ({ candidate, supporters: parts.filter((part) => plainCore(part).includes(candidate)) }))
            .filter(({ supporters }) => supporters.length >= 2 && supporters.length * 3 >= parts.length * 2)
            .sort((left, right) => right.supporters.length - left.supporters.length
            || right.candidate.length - left.candidate.length || left.candidate.localeCompare(right.candidate));
        const strongest = candidates[0];
        if (strongest)
            name = inferSharedName(strongest.supporters);
    }
    return name && name.length <= 80 ? name : undefined;
}
